import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Card,
  Typography,
  Spin,
  message,
  Button,
  Space,
  Popconfirm,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  HourglassOutlined,
  UserOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axiosInstance from "../axiosInstance";

const { Title, Text } = Typography;

const ManageEmployees = () => {
  const [loading, setLoading] = useState(false);
  const [claims, setClaims] = useState([]);

  const emp_data = localStorage.getItem("auth_user");
  const managerId = emp_data ? JSON.parse(emp_data).id : null;

  useEffect(() => {
    if (managerId) fetchClaims(managerId);
  }, [managerId]);

  // -----------------------------
  // Fetch all claims (manager view)
  // -----------------------------
  const fetchClaims = async (id, showMsg = false) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/get_claims.php?role=MANAGER&employee_id=${id}`
      );
      if (res.data.success) {
        setClaims(res.data.data || []);
        if (showMsg) message.success("Claims refreshed!");
      } else {
        message.error(res.data.message || "Failed to fetch claims");
      }
    } catch (err) {
      console.error(err);
      message.error("Error fetching claims");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Approve / Reject Claim
  // -----------------------------
  const handleClaimAction = async (claimId, status) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post("/approve_claim.php", {
        claim_id: claimId,
        manager_id: managerId,
        status,
      });

      if (res.data.success) {
        message.success(
          status === "APPROVED"
            ? "Claim approved successfully!"
            : "Claim rejected."
        );
        fetchClaims(managerId);
      } else {
        message.error(res.data.message || "Failed to update claim");
      }
    } catch (err) {
      console.error(err);
      message.error("Error processing claim action");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Table Columns
  // -----------------------------
  const columns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (name) => (
        <Space>
          <UserOutlined style={{ color: "#1677ff" }} />
          <Text strong>{name || "Unknown"}</Text>
        </Space>
      ),
    },
    {
      title: "Points",
      dataIndex: "claim_points",
      key: "claim_points",
      render: (points) => (
        <Text strong style={{ color: "#fa541c" }}>
          {points} pts
        </Text>
      ),
    },
    {
      title: "Reason",
      dataIndex: "claim_reason",
      key: "claim_reason",
      render: (reason) => <Text>{reason || "—"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color, icon;
        if (status === "APPROVED") {
          color = "green";
          icon = <CheckCircleOutlined />;
        } else if (status === "REJECTED") {
          color = "red";
          icon = <CloseCircleOutlined />;
        } else {
          color = "orange";
          icon = <HourglassOutlined />;
        }
        return (
          <Tag color={color} icon={icon} style={{ fontSize: 13 }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Manager",
      dataIndex: "manager_name",
      key: "manager_name",
      render: (name) => <Text>{name || "—"}</Text>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "Approved At",
      dataIndex: "approved_at",
      key: "approved_at",
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      render: (_, record) => {
        if (record.status === "PENDING") {
          return (
            <Space>
              <Popconfirm
                title="Approve this claim?"
                onConfirm={() => handleClaimAction(record.id, "APPROVED")}
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary" size="small">
                  Approve
                </Button>
              </Popconfirm>

              <Popconfirm
                title="Reject this claim?"
                onConfirm={() => handleClaimAction(record.id, "REJECTED")}
                okText="Yes"
                cancelText="No"
              >
                <Button danger size="small">
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Text type="secondary" style={{ fontStyle: "italic" }}>
            No action
          </Text>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          backgroundColor: "#fff",
        }}
      >
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Title level={4} style={{ marginBottom: 0 }}>
              Employee Claims Management
            </Title>
            <Text type="secondary">
              Approve or reject employee point redemption claims
            </Text>
          </div>
          <Button
            type="default"
            icon={<ReloadOutlined spin={loading} />}
            onClick={() => fetchClaims(managerId, true)}
          >
            Refresh
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Spin size="medium" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={claims}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} claims`,
              pageSizeOptions: ["10", "20", "50"],
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default ManageEmployees;
